import boardApi from "../../../services/rest/boardApi";
import {
  ApiHost,
  ApiPost,
  ApiPostsListData,
  ApiTopic,
} from "../types/api";
import {
  HostSaveDto,
  ListSessionsQuery,
  SessionReinstateDto,
  SessionRemoveDto,
  SessionSaveDto,
  TopicSaveDto,
  WebinarServiceError,
} from "../types/dto";
import {
  SessionStatusCounts,
  WebinarHost,
  WebinarSession,
  WebinarTopic,
} from "../types/models";
import { logWebinarPayload } from "../utils/logWebinarPayload";
import {
  mapApiHostToHost,
  mapApiPostToSession,
  mapApiTopicToTopic,
  mapHostSaveDtoToApiBody,
  mapSessionSaveDtoToApiBody,
  mapTopicSaveDtoToApiBody,
} from "./apiMappers";
import {
  HostImageUploadResult,
  uploadHostImage,
} from "./uploadHostImage";

export type ListSessionsPage = {
  sessions: WebinarSession[];
  counts: SessionStatusCounts;
  hosts: WebinarHost[];
  topics: WebinarTopic[];
  page: number;
  limit: number;
  total: number;
  pagination: Pagination;
};

export type Pagination = {
  page: number;
  limit: number;
  total: number;
};
type ApiEnvelope<T> = {
  code?: number;
  message?: string;
  data?: T;
};

const emptyCounts = (): SessionStatusCounts => ({
  all: 0,
  draft: 0,
  scheduled: 0,
  live: 0,
  ended: 0,
  cancelled: 0,
});

const emptyPostsList = (): ApiPostsListData => ({
  posts: [],
  counts: emptyCounts(),
});

function asArray<T>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  return [];
}

/** Unwrap plain API objects, nested keys, or Mongoose documents (`_doc`). */
function unwrapApiEntity<T extends { _id?: string }>(
  raw: unknown,
  nestedKey?: string
): T | null {
  if (!raw || typeof raw !== "object") return null;

  let entity = raw as Record<string, unknown>;
  if (
    nestedKey &&
    entity[nestedKey] &&
    typeof entity[nestedKey] === "object"
  ) {
    entity = entity[nestedKey] as Record<string, unknown>;
  }

  if (entity._doc && typeof entity._doc === "object") {
    const doc = entity._doc as Record<string, unknown>;
    return {
      ...doc,
      ...(typeof entity.avatarUrl === "string"
        ? { avatarUrl: entity.avatarUrl }
        : {}),
    } as T;
  }

  if (typeof entity._id === "string" && entity._id) {
    return entity as T;
  }

  return null;
}

function messageFromUnknown(err: unknown, fallback: string): string {
  if (err instanceof WebinarServiceError) return err.message;
  if (err && typeof err === "object") {
    const maybe = err as { message?: string; status?: number };
    if (typeof maybe.message === "string" && maybe.message.trim()) {
      const msg = maybe.message.trim();
      // Guard against leaking HTML / JSON-parse noise from proxies.
      if (
        msg.startsWith("<!DOCTYPE") ||
        msg.startsWith("<html") ||
        /Unexpected token/.test(msg) ||
        /is not valid JSON/i.test(msg)
      ) {
        return maybe.status === 404
          ? "Live sessions API not found"
          : "Could not reach the live sessions API";
      }
      return msg;
    }
    if (maybe.status === 404) return "Live sessions API not found";
  }
  if (err instanceof Error && err.message) {
    if (
      /Unexpected token/.test(err.message) ||
      /is not valid JSON/i.test(err.message)
    ) {
      return "Could not reach the live sessions API";
    }
    return err.message;
  }
  return fallback;
}

/** Parse plain Ajax.Request envelope from boardApi.js → base.js. */
async function fromApi<T>(promise: Promise<unknown>): Promise<T | null> {
  try {
    const response = (await promise) as ApiEnvelope<T>;
    if (typeof response === "string") {
      throw new WebinarServiceError(
        "VALIDATION",
        "Could not reach the live sessions API"
      );
    }
    if (Number(response?.code) !== 1) {
      throw new WebinarServiceError(
        "VALIDATION",
        response?.message || "Request failed"
      );
    }
    return (response.data ?? null) as T | null;
  } catch (err) {
    if (err instanceof WebinarServiceError) throw err;
    throw new WebinarServiceError(
      "VALIDATION",
      messageFromUnknown(err, "Request failed")
    );
  }
}

function buildPostsQuery(query?: ListSessionsQuery): {
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
} {
  const params: {
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  } = {};
  if (query?.status && query.status !== "all") {
    params.status = query.status;
  }
  if (query?.q?.trim()) {
    params.search = query.q.trim();
  }
  if (query?.page != null && query.page > 0) {
    params.page = query.page;
  }
  if (query?.limit != null && query.limit > 0) {
    params.limit = query.limit;
  }
  return params;
}

async function fetchPostsList(
  query?: ListSessionsQuery
): Promise<ApiPostsListData> {
  const data = await fromApi<ApiPostsListData | ApiPost[]>(
    boardApi.ListBoardPosts(buildPostsQuery(query))
  );
  if (!data) return emptyPostsList();
  if (Array.isArray(data)) {
    return {
      ...emptyPostsList(),
      posts: data,
      counts: { ...emptyCounts(), all: data.length },
      total: data.length,
      page: query?.page ?? 1,
      limit: query?.limit ?? data.length,
      pagination: {
        page: query?.page ?? 1,
        limit: query?.limit ?? data.length,
        total: data.length,
      },
    };
  }

  const pagination = data.pagination;
  const page =
    pagination?.page ?? data.page ?? query?.page ?? 1;
  const limit =
    pagination?.limit ?? data.limit ?? query?.limit;
  const total =
    typeof pagination?.total === "number"
      ? pagination.total
      : typeof data.total === "number"
        ? data.total
        : undefined;

  return {
    posts: asArray<ApiPost>(data.posts),
    counts: { ...emptyCounts(), ...(data.counts || {}) },
    page,
    limit,
    total,
    pagination:
      typeof total === "number"
        ? {
            page,
            limit: limit ?? query?.limit ?? 10,
            total,
          }
        : undefined,
  };
}

async function fetchHosts(): Promise<ApiHost[]> {
  const data = await fromApi<ApiHost[] | { hosts?: ApiHost[] }>(
    boardApi.ListBoardHosts()
  );
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return asArray<ApiHost>(data.hosts);
}

async function fetchTopics(): Promise<ApiTopic[]> {
  const data = await fromApi<ApiTopic[] | { topics?: ApiTopic[] }>(
    boardApi.ListBoardTopics()
  );
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return asArray<ApiTopic>(data.topics);
}

/**
 * Board API facade (Ajax.Request via boardApi.js).
 * List page loads posts only; hosts/topics are fetched in the create/edit drawer.
 */
export const webinarService = {
  async listSessionsPage(
    query?: ListSessionsQuery
  ): Promise<ListSessionsPage> {
    const postsData = await fetchPostsList(query);
    const topicsById = new Map<string, WebinarTopic>();
    const sessions = postsData.posts.map((post) => {
      // Prefer embedded host/topic on the post — no /hosts or /topics GET on list.
      if (post.topic?._id) {
        topicsById.set(post.topic._id, mapApiTopicToTopic(post.topic));
      }
      return mapApiPostToSession(post);
    });

    const pageNum = postsData.page ?? query?.page ?? 1;
    const limit = postsData.limit ?? query?.limit ?? sessions.length;
    const statusKey =
      query?.status && query.status !== "all"
        ? (query.status as keyof SessionStatusCounts)
        : "all";
    const totalFromCounts = postsData.counts?.[statusKey];
    // Prefer pagination.total from the API (authoritative for the current filter).
    const total =
      typeof postsData.total === "number"
        ? postsData.total
        : typeof totalFromCounts === "number"
          ? totalFromCounts
          : sessions.length;

    return {
      sessions,
      counts: postsData.counts,
      hosts: [],
      topics: Array.from(topicsById.values()),
      page: pageNum,
      limit,
      total,
      pagination: {
        page: pageNum,
        limit,
        total,
      },
    };
  },

  async listSessions(query?: ListSessionsQuery): Promise<WebinarSession[]> {
    const page = await webinarService.listSessionsPage(query);
    return page.sessions;
  },

  async getSession(id: string): Promise<WebinarSession | null> {
    const page = await webinarService.listSessionsPage();
    return page.sessions.find((s) => s.id === id) || null;
  },

  async saveSession(dto: SessionSaveDto): Promise<WebinarSession> {
    const body = mapSessionSaveDtoToApiBody(dto);
    logWebinarPayload(
      `session · ${dto.id ? "edit" : "create"} · draft`,
      body
    );

    const apiPost = dto.id
      ? await fromApi<ApiPost>(boardApi.PatchBoardPost(dto.id, body))
      : await fromApi<ApiPost>(boardApi.CreateBoardPost(body));

    if (!apiPost?._id) {
      throw new WebinarServiceError(
        "VALIDATION",
        dto.id ? "Session was not updated" : "Draft was not created"
      );
    }
    return mapApiPostToSession(apiPost);
  },

  async publishSession(id: string): Promise<WebinarSession> {
    const apiPost = await fromApi<ApiPost>(boardApi.PublishBoardPost(id));
    if (!apiPost?._id) {
      throw new WebinarServiceError("VALIDATION", "Session was not published");
    }
    return mapApiPostToSession(apiPost);
  },

  async removeSession(dto: SessionRemoveDto): Promise<void> {
    if (dto.kind === "delete_draft") {
      await fromApi<ApiPost>(boardApi.DeleteBoardPost(dto.id));
      return;
    }

    if (dto.kind === "cancel_scheduled") {
      const payload = {
        reason: dto.reason || "",
        memberNote: dto.note || "",
      };
      await fromApi<ApiPost>(boardApi.CancelBoardPost(dto.id, payload));
      return;
    }

    throw new WebinarServiceError(
      "INVALID_TRANSITION",
      "Unsupported remove action"
    );
  },

  async reinstateSession(
    _dto: SessionReinstateDto
  ): Promise<WebinarSession> {
    throw new WebinarServiceError(
      "UNAVAILABLE",
      "Reinstate is not available yet"
    );
  },

  async listHosts(): Promise<WebinarHost[]> {
    const apiHosts = await fetchHosts();
    return apiHosts.map(mapApiHostToHost);
  },

  async uploadHostImage(file: File): Promise<HostImageUploadResult> {
    return uploadHostImage(file);
  },

  async saveHost(dto: HostSaveDto): Promise<WebinarHost> {
    const body = mapHostSaveDtoToApiBody(dto);
    logWebinarPayload("host · save", body);
    const raw = dto.id
      ? await fromApi<unknown>(boardApi.PatchBoardHost(dto.id, body))
      : await fromApi<unknown>(boardApi.CreateBoardHost(body));
    const apiHost = unwrapApiEntity<ApiHost>(raw, "host");
    if (!apiHost?._id) {
      throw new WebinarServiceError(
        "VALIDATION",
        dto.id ? "Host was not updated" : "Host was not created"
      );
    }
    return mapApiHostToHost(apiHost);
  },

  async listTopics(): Promise<WebinarTopic[]> {
    const apiTopics = await fetchTopics();
    return apiTopics.map(mapApiTopicToTopic);
  },

  async addTopic(dto: TopicSaveDto): Promise<WebinarTopic> {
    const body = mapTopicSaveDtoToApiBody(dto);
    logWebinarPayload("topic · create", body);
    const raw = await fromApi<unknown>(boardApi.CreateBoardTopic(body));
    const apiTopic = unwrapApiEntity<ApiTopic>(raw, "topic");
    if (!apiTopic?._id) {
      throw new WebinarServiceError("VALIDATION", "Topic was not created");
    }
    return mapApiTopicToTopic(apiTopic);
  },
};
